import { templateParse } from "../../logic/templateParse.js";
import type { BootstrapOffcanvas } from "../../types/bootstrap.js";
import { DataBaseComponent } from "./data-base-component.js";

export class DataLinksComponent extends DataBaseComponent {
    static webComponentName: string = "data-links";
    protected _templateId: string = "data-links-component";
    
    // Offcanvas management
    private offcanvasInstance: BootstrapOffcanvas | null = null;
    private offcanvasElement: HTMLElement | null = null;
    private offcanvasId: string = "";
    
    constructor() {
        super();
        this.offcanvasId = `data-links-offcanvas-${this.createRandomId()}`;
    }
    protected onConnected(): void {
        super.onConnected();
        this.initializeOffcanvas();
        this.on("click",`[role="edit-links"]`, this.handleEditButton.bind(this));
        this.on("click",`[role="add-link"]`, this.handleAddLink.bind(this));
        this.on("click",`[role="remove-link"]`, this.handleRemoveLink.bind(this));
        this.on("input",`[data-link-key-input]`, this.handleInputChange.bind(this));
        this.on("input",`[data-link-url-input]`, this.handleInputChange.bind(this));
    }
    protected render(): void {
        this.renderMainView();
        this.renderOffcanvasContent();
        this.afterRender();
    }
    
    private renderMainView(): void {
        // Render main component template only
        const mainData = this.addDataForTemplate();
        const mainHtml = templateParse(this.templateId, mainData);
        if (mainHtml) {
            this.innerHTML = mainHtml;
        }
    }
    
    private renderOffcanvasContent(): void {
        // Render edit template in offcanvas if it exists
        if (this.offcanvasElement) {
            const editContainer = this.offcanvasElement.querySelector('.links-edit-container');
            if (editContainer) {
                const mainData = this.addDataForTemplate();
                const editHtml = templateParse('data-links-edit-template', mainData);
                if (editHtml) {
                    editContainer.innerHTML = editHtml;
                    // Setup event listeners after content is rendered
                    this.setupOffcanvasContentEvents();
                }
            }
        }
    }
    
    addDataForTemplate(): Record<string, any> {
        // Parse value as Record<string,string> or default to empty object
        let linksData: Record<string, string> = {};
        
        if (this.value) {
            if (typeof this.value === 'object' && this.value !== null) {
                linksData = this.value as Record<string, string>;
            } else if (typeof this.value === 'string') {
                try {
                    linksData = JSON.parse(this.value);
                } catch (e) {
                    console.warn('DataLinksComponent: Invalid JSON in value', this.value);
                    linksData = {};
                }
            }
        }
        
        // Convert to array for easier template iteration
        const linksArray = Object.entries(linksData).map(([key, url]) => ({
            key,
            url
        }));
        return {
            field: this.field,
            action: this.action,
            readonly: this.readOnly,
            linksData,
            linksArray,
            offcanvasId: this.offcanvasId
        };
    }
    
    private handleAddLink(event: Event): void {
        event.preventDefault();
        
        // Search in both component and offcanvas
        const keyInput = this.findElement('[role="link-key-input"]') as HTMLInputElement;
        const urlInput = this.findElement('[role="link-url-input"]') as HTMLInputElement;
        
        if (!keyInput || !urlInput) return;
        // Use HTML5 validation
        if (!keyInput.checkValidity() || !urlInput.checkValidity()) {
            keyInput.reportValidity();
            urlInput.reportValidity();
            return;
        }
        
        const key = keyInput.value.trim();
        const url = urlInput.value.trim();
        
        // Update the value
        const currentLinks = this.getLinksFromValue();
        currentLinks[key] = url;
        
        this.updateValue(currentLinks);
        
        // Clear inputs
        keyInput.value = '';
        urlInput.value = '';
        
        // Re-render both main view and offcanvas
        this.render();
    }
    
    private handleRemoveLink(event: Event): void {
        event.preventDefault();
        
        const button = event.target as HTMLButtonElement;
        const row = button.closest('[data-link-row]');
        if (!row) return;
        
        // Find the index of this row - search in both places
        const container = this.findElement('.links-container') || 
                         this.findElement('.existing-links');
        if (!container) return;
        
        const rows = container.querySelectorAll('[data-link-row]');
        const index = Array.from(rows).indexOf(row);
        
        if (index === -1) return;
        
        const currentLinks = this.getLinksFromValue();
        const keys = Object.keys(currentLinks);
        
        if (index >= 0 && index < keys.length) {
            const keyToRemove = keys[index];
            delete currentLinks[keyToRemove];
            this.updateValue(currentLinks);
            // Re-render both main view and offcanvas
            this.render();
        }
    }
    
    private handleInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const row = input.closest('[data-link-row]');
        if (!row) {
            console.log('DataLinksComponent: Input change without row context');
            return;
        }
        
        const keyInput = row.querySelector('[data-link-key-input]') as HTMLInputElement;
        const urlInput = row.querySelector('[data-link-url-input]') as HTMLInputElement;
        
        if (!keyInput || !urlInput) {
            console.warn('DataLinksComponent: Key or URL input not found in row');
            return;}
        
        // Only proceed if both inputs are valid
        if (!keyInput.checkValidity() || !urlInput.checkValidity()) {
            return;
        }
        
        // Find the index of this row - search in both places
        const container = this.findElement('.links-container') || 
                         this.findElement('.existing-links');
        if (!container) {
            console.warn('DataLinksComponent: Links container not found');
            return;
        }
        
        const rows = container.querySelectorAll('[data-link-row]');
        const index = Array.from(rows).indexOf(row);
        
        if (index === -1) {
            console.warn('DataLinksComponent: Row not found in container');
            return;
        }
        
        const newKey = keyInput.value.trim();
        const newUrl = urlInput.value.trim();
        
        if (!newKey || !newUrl) {
            console.warn('DataLinksComponent: Key or URL is empty');
            return;
        }
        const currentLinks = this.getLinksFromValue();
        const keys = Object.keys(currentLinks);
        if (index >= 0 && index < keys.length) {
            // Create new links object maintaining order
            const newLinks: Record<string, string> = {};
            keys.forEach((key, i) => {
                if (i === index) {
                    newLinks[newKey] = newUrl;
                } else {
                    newLinks[key] = currentLinks[key];
                }
            });
            
            this.updateValue(newLinks);
            // Update only main view to show changes, avoid re-rendering offcanvas to preserve focus
            this.renderMainView();
        }
    }
    
    private getLinksFromValue(): Record<string, string> {
        if (!this.value) return {};
        
        if (typeof this.value === 'object' && this.value !== null) {
            return { ...this.value as Record<string, string> };
        }
        
        if (typeof this.value === 'string') {
            try {
                return JSON.parse(this.value);
            } catch (e) {
                return {};
            }
        }
        
        return {};
    }
    
    private updateValue(links: Record<string, string>): void {
        this.value = links;
        
        // Dispatch change event
        this.dispatchEvent(new CustomEvent('form-change', {
            detail: { value: links },
            bubbles: true
        }));
    }
    
    // =============================
    // Helper Methods
    // =============================
    
    /**
     * Find element in both component and offcanvas
     */
    private findElement(selector: string): Element | null {
        // First try in component
        let element = this.querySelector(selector);
        if (element) return element;
        
        // Then try in offcanvas
        if (this.offcanvasElement) {
            element = this.offcanvasElement.querySelector(selector);
            if (element) return element;
        }
        
        return null;
    }
    
    // =============================
    // Offcanvas Management
    // =============================
    
    private initializeOffcanvas(): void {
        // Create offcanvas structure and append to body
        const offcanvasData = this.addDataForTemplate();
        const offcanvasHtml = templateParse('data-links-offcanvas-template', offcanvasData);
        
        if (offcanvasHtml) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = offcanvasHtml;
            this.offcanvasElement = tempDiv.firstElementChild as HTMLElement;
            document.body.appendChild(this.offcanvasElement);
            
            // Initialize Bootstrap Offcanvas
            this.offcanvasInstance = new window.bootstrap.Offcanvas(this.offcanvasElement, {
                backdrop: true,
                keyboard: true,
                scroll: false
            });
            
            // Setup offcanvas event listeners
            this.setupOffcanvasEvents();
            
            this.debugLog('Offcanvas initialized:', this.offcanvasId);
        }
    }
    
    private setupOffcanvasEvents(): void {
        if (!this.offcanvasElement) return;
        
        // Auto-save on hide
        this.offcanvasElement.addEventListener('hide.bs.offcanvas', () => {
            this.debugLog('Offcanvas closing - auto-save triggered');
            // Value is already updated by handleInputChange, no additional action needed
        });
    }
    
    private setupOffcanvasContentEvents(): void {
        if (!this.offcanvasElement) return;
        
        // Remove existing listeners to avoid duplicates
        const existingClickHandler = (this as any)._offcanvasClickHandler;
        const existingInputHandler = (this as any)._offcanvasInputHandler;
        
        if (existingClickHandler) {
            this.offcanvasElement.removeEventListener('click', existingClickHandler);
        }
        if (existingInputHandler) {
            this.offcanvasElement.removeEventListener('input', existingInputHandler);
        }
        
        // Create new handlers and store references
        const clickHandler = (event: Event) => {
            const target = event.target as HTMLElement;
            
            // Handle add link button
            if (target.closest('[role="add-link"]')) {
                this.handleAddLink(event);
                return;
            }
            
            // Handle remove link button
            if (target.closest('[role="remove-link"]')) {
                this.handleRemoveLink(event);
                return;
            }
        };
        
        const inputHandler = (event: Event) => {
            const target = event.target as HTMLElement;
            if (target.matches('[data-link-key-input]') || target.matches('[data-link-url-input]')) {
                this.handleInputChange(event);
            }
        };
        
        // Store references and add listeners
        (this as any)._offcanvasClickHandler = clickHandler;
        (this as any)._offcanvasInputHandler = inputHandler;
        
        this.offcanvasElement.addEventListener('click', clickHandler);
        this.offcanvasElement.addEventListener('input', inputHandler);
    }
    
    private handleEditButton(event: Event): void {
        event.preventDefault();
        
        if (this.offcanvasInstance) {
            // Re-render edit content before showing
            this.renderEditContent();
            this.offcanvasInstance.show();
        }
    }
    
    private renderEditContent(): void {
        this.renderOffcanvasContent();
    }
    
    protected onDisconnected(): void {
        super.onDisconnected();
        
        // Cleanup offcanvas
        if (this.offcanvasInstance) {
            this.offcanvasInstance.dispose();
            this.offcanvasInstance = null;
        }
        
        if (this.offcanvasElement) {
            this.offcanvasElement.remove();
            this.offcanvasElement = null;
        }
    }
}